import { gql, useMutation, useQuery } from '@apollo/client';
import LoadingIndicator from '@orn/shared-web/components/LoadingIndicator/LoadingIndicator';
import Tooltip, { TooltipTrigger } from '@orn/shared-web/components/Tooltip/Tooltip';
import * as React from 'react';

import { ChecklistSection, ReleasePreviewStatus } from '../../__generated__/globalTypes';
import Button from '../../components/Button';
import Header from '../../components/Header';
import Table from '../../components/Table';
import { useToast } from '../../components/Toast';
import { IS_DEV } from '../../env';
import { useAuthState } from '../../providers/AuthProvider';

import CiStatusBadge from './components/CiStatusBadge';
import CommitsBlock from './components/CommitsBlock';
import CurrentDraftCard from './components/CurrentDraftCard';
import HistoryRow from './components/HistoryRow';
import InlineMarkdown from './components/InlineMarkdown';
import PrerequisitesChecklist from './components/PrerequisitesChecklist';
import RiskCalloutsBanner from './components/RiskCalloutsBanner';
import TestPlanChecklist from './components/TestPlanChecklist';
import {
  deriveAnnounceButtonState,
  formatChecklistMarker,
  formatPostedAtClockTime,
  isMultipleDraftsError,
  mapDraftMutationError,
  RELEASE_DRAFT_POLL_INTERVAL_MS,
  SHORT_SHA_LENGTH,
  sha256Hex,
} from './releaseLogic';

import type {
  AdminGetReleaseChangeLogQuery,
  AdminGetReleasePreviewQuery,
  AdminGetReleasePreviewQueryVariables,
  AdminStartReleasePreviewMutation,
  AdminGetCurrentReleaseDraftQuery,
  AdminListReleasesQuery,
  AdminAnnouncePreReleaseMutation,
  AdminAnnouncePreReleaseMutationVariables,
  AdminPublishReleaseDraftMutation,
  AdminPublishReleaseDraftMutationVariables,
  AdminDeleteReleaseDraftMutation,
  AdminDeleteReleaseDraftMutationVariables,
  AdminToggleReleaseChecklistItemMutation,
  AdminToggleReleaseChecklistItemMutationVariables,
} from './__generated__/ReleasesScene.gql';
import type { IAnnounceJustPosted } from './releaseLogic';

/**
 * Mirrors the env gate in `@orn/mops-api`'s `SlackReleaseNotifier` —
 * dev posts to `#production-deploys-test` so an admin exercising the
 * tool against their dev box cannot page on-call. Staging and prod
 * both target the real channel.
 */
const SLACK_CHANNEL = IS_DEV ? '#production-deploys-test' : '#production-deploys';

/**
 * Fragment for the `ReleasePreview` shape, shared between the start mutation
 * and the polling query. Both operations select the same fields when the
 * status is COMPLETED so the rendering code is uniform.
 */
const RELEASE_PREVIEW_FIELDS = gql`
  fragment ReleasePreviewFields on ReleasePreview {
    baseTag
    baseSha
    headSha
    expectedHeadSha
    computedNextTag
    summary
    prerequisites {
      text
      prNumbers
    }
    riskCallouts
    testPlan {
      action
      expectedResult
    }
    ciStatus {
      conclusion
      totalCount
      successCount
      failureCount
      pendingCount
    }
    commits {
      sha
      shortSha
      subject
      author
      authoredAt
      prNumber
      prUrl
    }
  }
`;

const ADMIN_START_RELEASE_PREVIEW = gql`
  mutation AdminStartReleasePreview {
    adminStartReleasePreview {
      status
      baseSha
      headSha
      preview {
        ...ReleasePreviewFields
      }
    }
  }
  ${RELEASE_PREVIEW_FIELDS}
`;

const ADMIN_GET_RELEASE_PREVIEW = gql`
  query AdminGetReleasePreview($baseSha: String!, $headSha: String!) {
    adminGetReleasePreview(baseSha: $baseSha, headSha: $headSha) {
      status
      baseSha
      headSha
      preview {
        ...ReleasePreviewFields
      }
    }
  }
  ${RELEASE_PREVIEW_FIELDS}
`;

const ADMIN_GET_RELEASE_CHANGE_LOG = gql`
  query AdminGetReleaseChangeLog {
    adminGetReleaseChangeLog {
      baseTag
      baseSha
      headSha
      computedNextTag
      ciStatus {
        conclusion
        totalCount
        successCount
        failureCount
        pendingCount
      }
      commits {
        sha
        shortSha
        subject
        author
        authoredAt
        prNumber
        prUrl
      }
    }
  }
`;

const ADMIN_GET_CURRENT_RELEASE_DRAFT = gql`
  query AdminGetCurrentReleaseDraft {
    adminGetCurrentReleaseDraft {
      tag
      targetSha
      htmlUrl
      draftedByEmail
      draftedAt
      body
      prerequisiteCheckedIndices
      testPlanCheckedIndices
      prerequisiteLineContents
      testPlanLineContents
      prerequisiteCheckedMarkers
      testPlanCheckedMarkers
    }
  }
`;

const ADMIN_LIST_RELEASES = gql`
  query AdminListReleases {
    adminListReleases {
      tag
      sha
      body
      mopsAuthorEmail
      githubAuthorLogin
      createdAt
    }
  }
`;

const ADMIN_ANNOUNCE_PRE_RELEASE = gql`
  mutation AdminAnnouncePreRelease($expectedHeadSha: String!) {
    adminAnnouncePreRelease(expectedHeadSha: $expectedHeadSha) {
      draftTag
      draftUrl
      slackMessageUrl
    }
  }
`;

const ADMIN_PUBLISH_RELEASE_DRAFT = gql`
  mutation AdminPublishReleaseDraft($expectedDraftTag: String!) {
    adminPublishReleaseDraft(expectedDraftTag: $expectedDraftTag) {
      tag
      sha
      createdAt
    }
  }
`;

const ADMIN_DELETE_RELEASE_DRAFT = gql`
  mutation AdminDeleteReleaseDraft($expectedDraftTag: String!) {
    adminDeleteReleaseDraft(expectedDraftTag: $expectedDraftTag) {
      deletedTag
    }
  }
`;

const ADMIN_TOGGLE_RELEASE_CHECKLIST_ITEM = gql`
  mutation AdminToggleReleaseChecklistItem(
    $expectedDraftTag: String!
    $section: ChecklistSection!
    $index: Int!
    $checked: Boolean!
    $expectedLineHash: String!
  ) {
    adminToggleReleaseChecklistItem(
      expectedDraftTag: $expectedDraftTag
      section: $section
      index: $index
      checked: $checked
      expectedLineHash: $expectedLineHash
    ) {
      tag
      targetSha
      htmlUrl
      draftedByEmail
      draftedAt
      body
      prerequisiteCheckedIndices
      testPlanCheckedIndices
      prerequisiteLineContents
      testPlanLineContents
      prerequisiteCheckedMarkers
      testPlanCheckedMarkers
    }
  }
`;

/**
 * Empirically, GitHub's `listReleases` reflects a freshly-created
 * draft within a few seconds of `createRelease`. 10s is a generous
 * margin that has been reliable in staging; if we ever observe
 * timeouts longer than this, switch to a polling loop with a cap.
 */
const DRAFT_PROPAGATION_DELAY_MS = 10_000;

/** Apollo poll cadence for `adminGetReleasePreview` (cache-status check). */
const PREVIEW_POLL_INTERVAL_MS = 3_000;
/**
 * Hard ceiling on how long we'll keep polling for the LLM cache row to
 * appear. After this, we surface "still generating, refresh to retry"
 * rather than silently re-querying for hours on a forgotten tab. The
 * worker reliably writes the cache row in 30–60 s; 90 s gives ample
 * margin for outliers without burning the operator's tab indefinitely.
 */
const PREVIEW_POLL_CAP_MS = 90_000;

/**
 * Releases admin tool. Three stacked sections:
 *
 *  1. **Draft card** — surfaces the currently-open MOPS draft on GitHub,
 *     if any. Wires `adminPublishReleaseDraft` and `adminDeleteReleaseDraft`
 *     behind two danger buttons + two confirmation modals, gated by an
 *     interactive checklist over the LLM-generated `prerequisites` and
 *     `testPlan` (see `ReleaseChecklistGate`). Auto-fires the preview
 *     query when a draft is present so the checklist data is available.
 *  2. **Pending release** — preview of what `baseTag → headSha` would tag as,
 *     including the LLM summary, risk callouts, prerequisites checklist,
 *     suggested test plan, CI status, and the merged commits since the base
 *     tag. The "Announce pre-release" button creates the GitHub draft + posts
 *     Slack; the button's exact label and enable/disable state is derived
 *     from the open-draft state in `releaseLogic.deriveAnnounceButtonState`.
 *  3. **Release history** — recent GitHub releases. MOPS-authored releases
 *     show the operator email parsed from the audit footer; legacy
 *     `release.yml` releases show `—`.
 */
const ReleasesScene = () => {
  const { showToast } = useToast();

  // Release-notes preview now runs as a `start mutation + polling query`
  // pair so the slow Anthropic call (30-60 s) runs off the synchronous
  // request path on `@orn/api`'s `_async` worker — `@orn/mops-api`'s 30 s
  // API Gateway timeout never sees it. The mutation snapshots a
  // `(baseSha, headSha)` pair; the query polls the LLM cache for that
  // pair every 3 s until the worker writes the row.
  const [pollingTimedOut, setPollingTimedOut] = React.useState(false);
  const [startedShas, setStartedShas] = React.useState<{ baseSha: string; headSha: string } | null>(null);
  const [startedState, setStartedState] = React.useState<
    AdminStartReleasePreviewMutation['adminStartReleasePreview'] | null
  >(null);

  const [startReleasePreview, { error: startError }] =
    useMutation<AdminStartReleasePreviewMutation>(ADMIN_START_RELEASE_PREVIEW);

  const {
    data: pollData,
    error: pollError,
    stopPolling,
  } = useQuery<AdminGetReleasePreviewQuery, AdminGetReleasePreviewQueryVariables>(ADMIN_GET_RELEASE_PREVIEW, {
    variables: startedShas ?? { baseSha: '', headSha: '' },
    // `skip` keeps Apollo from issuing requests pre-Start. Polling stops
    // once we transition to COMPLETED via the effect below — both halt
    // mechanisms are coordinated so a stale poll doesn't fire after the
    // operator has already moved on.
    skip: !startedShas || pollingTimedOut,
    pollInterval: PREVIEW_POLL_INTERVAL_MS,
    notifyOnNetworkStatusChange: false,
  });
  const polledStatus = pollData?.adminGetReleasePreview?.status ?? null;

  // Stop polling as soon as a COMPLETED row arrives. Apollo's poll loop
  // doesn't auto-stop on data shape; we trip it imperatively here so the
  // tab doesn't keep hitting `/graphql` after the operator has the
  // preview rendered.
  React.useEffect(() => {
    if (polledStatus === ReleasePreviewStatus.Completed) {
      stopPolling();
    }
  }, [polledStatus, stopPolling]);

  // 90s polling cap: if the worker hasn't written the cache row by then,
  // the UI flips to a "still generating, refresh to check" state rather
  // than polling forever on a forgotten tab.
  React.useEffect(() => {
    if (!startedShas) {
      return undefined;
    }
    if (startedState?.status === ReleasePreviewStatus.Completed || polledStatus === ReleasePreviewStatus.Completed) {
      return undefined;
    }
    const timer = setTimeout(() => setPollingTimedOut(true), PREVIEW_POLL_CAP_MS);
    return () => clearTimeout(timer);
  }, [startedShas, startedState?.status, polledStatus]);

  /**
   * Kicks off (or restarts) the preview-generation flow. Resets the timeout
   * and the start-mutation snapshot so a Retry click swaps the scene back
   * to the spinner immediately. Apollo's errorLink reports failures to
   * Sentry; the inline error UI keys off `startError` / `pollError` /
   * `pollingTimedOut`.
   */
  const triggerPreview = React.useCallback(async (): Promise<void> => {
    setPollingTimedOut(false);
    setStartedShas(null);
    setStartedState(null);
    try {
      const result = await startReleasePreview();
      const state = result.data?.adminStartReleasePreview;
      if (!state) {
        return;
      }
      setStartedState(state);
      setStartedShas({ baseSha: state.baseSha, headSha: state.headSha });
    } catch {
      // ApolloProvider's errorLink reports to Sentry; the inline error
      // panel renders against `startError`.
    }
  }, [startReleasePreview]);

  const previewState = pollData?.adminGetReleasePreview ?? startedState;
  const previewCalled = startedShas !== null;
  const previewLoading =
    previewCalled &&
    previewState?.status !== ReleasePreviewStatus.Completed &&
    !pollingTimedOut &&
    !pollError &&
    !startError;
  const previewError = startError ?? pollError ?? null;
  const refetchPreview = triggerPreview;

  // The lightweight changelog runs on mount — it doesn't trigger the LLM,
  // just a few GitHub round-trips, so the page can show operators what's
  // pending before they commit to starting a draft.
  const { data: changeLogData, loading: changeLogLoading } =
    useQuery<AdminGetReleaseChangeLogQuery>(ADMIN_GET_RELEASE_CHANGE_LOG);

  const {
    data: draftData,
    loading: draftLoading,
    refetch: refetchDraft,
  } = useQuery<AdminGetCurrentReleaseDraftQuery>(ADMIN_GET_CURRENT_RELEASE_DRAFT, {
    // Cross-tab sync: another release manager's toggle becomes visible
    // here within ~30s without a push channel. `notifyOnNetworkStatusChange`
    // stays off so the silent polls don't flicker any loading state.
    pollInterval: RELEASE_DRAFT_POLL_INTERVAL_MS,
    notifyOnNetworkStatusChange: false,
  });

  const {
    data: historyData,
    loading: historyLoading,
    refetch: refetchHistory,
  } = useQuery<AdminListReleasesQuery>(ADMIN_LIST_RELEASES);

  const [announce, { loading: announceLoading }] = useMutation<
    AdminAnnouncePreReleaseMutation,
    AdminAnnouncePreReleaseMutationVariables
  >(ADMIN_ANNOUNCE_PRE_RELEASE);

  const [publishDraft] = useMutation<AdminPublishReleaseDraftMutation, AdminPublishReleaseDraftMutationVariables>(
    ADMIN_PUBLISH_RELEASE_DRAFT
  );

  const [deleteDraft] = useMutation<AdminDeleteReleaseDraftMutation, AdminDeleteReleaseDraftMutationVariables>(
    ADMIN_DELETE_RELEASE_DRAFT
  );

  const [toggleChecklistItem] = useMutation<
    AdminToggleReleaseChecklistItemMutation,
    AdminToggleReleaseChecklistItemMutationVariables
  >(ADMIN_TOGGLE_RELEASE_CHECKLIST_ITEM);

  // Operator's email — used to format the optimistic checklist marker
  // ("Checked by **localPart** at …") so the UI shows the attribution
  // the same frame the click lands. The server re-formats from
  // `vc.user.email` and the strings match byte-for-byte.
  const { user } = useAuthState();
  const operatorEmail = user?.email ?? '';

  const [justPosted, setJustPosted] = React.useState<IAnnounceJustPosted | null>(null);
  /**
   * Latched when the publish or delete mutation rejects with
   * `MULTIPLE_DRAFTS_FOUND`. Drives the recovery banner on the draft
   * card and disables Publish until the operator walks the queue down
   * via Discard. Cleared on every successful mutation or refetch.
   */
  const [multipleDraftsDetected, setMultipleDraftsDetected] = React.useState(false);
  /**
   * True between the announce mutation resolving and the post-delay
   * refetch landing. Keeps the Announce button visually pinned in its
   * loading state through GitHub's read-after-write window so the
   * operator can't double-click while we're waiting for the draft to
   * surface in `listReleases`.
   */
  const [isWaitingForDraftPropagation, setIsWaitingForDraftPropagation] = React.useState(false);

  const preview = previewState?.preview ?? null;
  const changeLog = changeLogData?.adminGetReleaseChangeLog;
  const draft = draftData?.adminGetCurrentReleaseDraft ?? null;
  const history = historyData?.adminListReleases ?? [];

  // Auto-trigger preview when a draft is open. The interactive
  // checklist gate on the draft card needs the structured prereqs and
  // testPlan from `adminGetReleasePreview` — the LLM cache hits because
  // the draft was just announced (or is being viewed shortly after),
  // so this typically lands instantly. If the operator refreshes hours
  // later and the cache has been evicted, they pay the 30-60s
  // regeneration cost; the gate shows "Loading checklist…" in the
  // meantime and Publish stays disabled.
  React.useEffect(() => {
    if (draft && !previewCalled) {
      void triggerPreview();
    }
  }, [draft, previewCalled, triggerPreview]);

  /**
   * Spinner shared by the in-flight load and the brief post-response
   * interstitial (response arrived but Apollo hasn't written the cache
   * yet). The "this can take a while" copy is load-bearing: cold cache
   * misses against the release-notes model regularly run 30-60s and
   * operators wonder if the page is broken.
   */
  const renderGeneratingSpinner = () => (
    <div className="flex flex-col items-center gap-4 px-6 py-12">
      <LoadingIndicator type="line-spinner" size="lg" />
      <div className="flex max-w-md flex-col items-center gap-1 text-center">
        <h2 className="text-fg-primary text-base font-semibold">Generating release notes…</h2>
        <p className="text-fg-secondary text-sm">
          This can take 30 to 60 seconds while we summarize commits since the last release. Hang tight — leave the page
          open.
        </p>
      </div>
    </div>
  );

  const buttonState = preview
    ? deriveAnnounceButtonState({
        expectedHeadSha: preview.expectedHeadSha,
        draftTargetSha: draft?.targetSha ?? null,
        slackChannel: SLACK_CHANNEL,
      })
    : null;

  const handleAnnounce = async () => {
    if (!preview || !buttonState || buttonState.mode === 'disabled') {
      return;
    }
    try {
      const result = await announce({ variables: { expectedHeadSha: preview.expectedHeadSha } });
      const data = result.data?.adminAnnouncePreRelease;
      setJustPosted({ postedAt: new Date(), slackUrl: data?.slackMessageUrl ?? null });
      // `handleAnnounce` is only mounted when no draft is open, so
      // `buttonState.mode` is always `'announce'` here. The `'repost-slack'`
      // mode is handled by `CurrentDraftCard` calling `handleRepostSlack`.
      showToast({ type: 'success', message: `Draft ${data?.draftTag} announced.` });
      // GitHub's `listReleases` lags `createRelease` by a few seconds.
      // Refetching immediately race-conditions with that window and
      // returns null for the brand-new draft, leaving the operator
      // stuck on the pending-release preview. Pause for the propagation
      // window before refetching so the first refetch surfaces the
      // draft and the page swaps to `CurrentDraftCard`.
      setIsWaitingForDraftPropagation(true);
      await new Promise((resolve) => setTimeout(resolve, DRAFT_PROPAGATION_DELAY_MS));
      // Refetch refreshes `expectedHeadSha` so a subsequent announce after
      // the operator backs out of the draft picks up any HEAD movement.
      // Guarded by `previewCalled` because Apollo's `useLazyQuery.refetch`
      // throws "Refetch called on a query that has not been executed" if
      // invoked before the first `triggerPreview()`. Today this branch is
      // reached only when `preview` is non-null (so `previewCalled` is
      // also true), but the explicit guard means a future refactor that
      // shifts how this handler mounts can't silently regress into a
      // throw-on-refetch.
      await Promise.all([refetchDraft(), refetchHistory(), previewCalled ? refetchPreview() : Promise.resolve()]);
      setIsWaitingForDraftPropagation(false);
    } catch {
      // ApolloProvider's errorLink reports GraphQL + network errors to Sentry.
      setIsWaitingForDraftPropagation(false);
      showToast({ type: 'error', message: 'Failed to announce pre-release. Please try again.' });
    }
  };

  /**
   * Re-post the Slack announcement for the open draft. Goes through the
   * same `announcePreRelease` mutation as the initial announce — the
   * server treats a second call at the same `targetSha` as idempotent
   * (skips creating a duplicate draft, just re-posts Slack).
   */
  const handleRepostSlack = async () => {
    if (!draft) {
      return;
    }
    try {
      const result = await announce({ variables: { expectedHeadSha: draft.targetSha } });
      const data = result.data?.adminAnnouncePreRelease;
      setJustPosted({ postedAt: new Date(), slackUrl: data?.slackMessageUrl ?? null });
      showToast({ type: 'success', message: 'Re-posted to Slack.' });
      await refetchDraft();
    } catch {
      showToast({ type: 'error', message: 'Failed to re-post Slack. Please try again.' });
    }
  };

  /**
   * Publishes the open draft. Server validates `expectedDraftTag`,
   * re-checks CI at `target_commitish`, then promotes the draft on
   * GitHub (which triggers `release.yml` → prod deploy). Server errors
   * map to operator-facing toast copy via `mapDraftMutationError`.
   *
   * Throws on failure so `DangerConfirmDialog` keeps itself open and
   * preserves the typed-confirmation value, letting the operator retry
   * without re-typing the tag.
   */
  const handlePublishDraft = async () => {
    if (!draft) {
      return;
    }
    try {
      await publishDraft({ variables: { expectedDraftTag: draft.tag } });
      setMultipleDraftsDetected(false);
      showToast({ type: 'success', message: `Published ${draft.tag}.` });
      await Promise.all([refetchDraft(), refetchHistory(), previewCalled ? refetchPreview() : Promise.resolve()]);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      if (isMultipleDraftsError(message)) {
        setMultipleDraftsDetected(true);
      }
      showToast({ type: 'error', message: mapDraftMutationError(message) });
      throw error;
    }
  };

  /**
   * Toggle a single checklist item. Apollo writes the mutation response
   * (the canonical `ReleaseDraft` shape, including refreshed checked-index
   * arrays) into cache via the default behavior, so the UI updates without
   * a follow-up fetch in this tab. Other tabs pick up the change on the
   * next 30s poll.
   *
   * `optimisticResponse` synthesizes a tentative `ReleaseDraft` with the
   * targeted index toggled and the marker text formatted client-side
   * (using the current operator's email + `now`) so the "Checked by …"
   * line appears the same frame the click lands. The marker formatter
   * mirrors the server's byte-for-byte, so when the real response
   * arrives a moment later the visible marker doesn't flicker.
   *
   * Errors map through `mapDraftMutationError` to operator-facing toast
   * copy. `STALE_CHECKLIST` triggers a draft refetch so the operator's
   * next click sees the up-to-date body.
   */
  const handleToggleChecklistItem = React.useCallback(
    async (section: ChecklistSection, index: number, checked: boolean, lineContent: string) => {
      if (!draft) {
        return;
      }
      try {
        const expectedLineHash = await sha256Hex(lineContent);
        const operatorLocalPart = operatorEmail.split('@')[0] || 'unknown';
        const isPrereq = section === ChecklistSection.Prerequisite;
        const indices = isPrereq ? draft.prerequisiteCheckedIndices : draft.testPlanCheckedIndices;
        const markers = isPrereq ? draft.prerequisiteCheckedMarkers : draft.testPlanCheckedMarkers;
        const nextIndices = checked
          ? Array.from(new Set([...indices, index])).sort((a, b) => a - b)
          : indices.filter((i) => i !== index);
        const nextMarkers = markers.map((m, i) =>
          i === index ? (checked ? formatChecklistMarker(operatorLocalPart, new Date()) : '') : m
        );
        const optimisticDraft: AdminToggleReleaseChecklistItemMutation['adminToggleReleaseChecklistItem'] = {
          ...draft,
          prerequisiteCheckedIndices: isPrereq ? nextIndices : draft.prerequisiteCheckedIndices,
          testPlanCheckedIndices: isPrereq ? draft.testPlanCheckedIndices : nextIndices,
          prerequisiteCheckedMarkers: isPrereq ? nextMarkers : draft.prerequisiteCheckedMarkers,
          testPlanCheckedMarkers: isPrereq ? draft.testPlanCheckedMarkers : nextMarkers,
        };
        await toggleChecklistItem({
          variables: { expectedDraftTag: draft.tag, section, index, checked, expectedLineHash },
          optimisticResponse: {
            __typename: 'Mutation',
            adminToggleReleaseChecklistItem: optimisticDraft,
          },
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        showToast({ type: 'error', message: mapDraftMutationError(message) });
        // Always refetch on error so the gate's optimistic-intent overlay
        // reconciles against the canonical body. Without this, a
        // GITHUB_UPDATE_FAILED leaves the box visually flipped until the
        // next 30s poll lands.
        await refetchDraft();
      }
    },
    [draft, toggleChecklistItem, showToast, refetchDraft, operatorEmail]
  );

  /**
   * Discards the open draft. Targets the front-of-queue draft on the
   * server side; the operator clicks again until only one remains
   * during MULTIPLE_DRAFTS_FOUND recovery. Refetches the draft query
   * to surface the next state.
   */
  const handleDiscardDraft = async () => {
    if (!draft) {
      return;
    }
    try {
      await deleteDraft({ variables: { expectedDraftTag: draft.tag } });
      setMultipleDraftsDetected(false);
      showToast({ type: 'success', message: `Discarded ${draft.tag}.` });
      await refetchDraft();
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      if (isMultipleDraftsError(message)) {
        setMultipleDraftsDetected(true);
      }
      showToast({ type: 'error', message: mapDraftMutationError(message) });
      throw error;
    }
  };

  return (
    <div className="bg-bg-primary text-fg-primary flex min-h-screen flex-col">
      <Header />
      <main className="flex flex-1 flex-col gap-6 px-6 py-5">
        <h1 className="text-sm font-semibold uppercase tracking-widest">Releases</h1>

        {/*
          When a MOPS draft is open, the **draft** is the actionable state —
          the preview at HEAD is no longer the operator's decision point.
          Show the Current Draft card and hide the Pending release preview;
          when no draft is open, show the preview as today.
        */}
        {draft ? (
          <CurrentDraftCard
            draft={draft}
            onRepostSlack={() => void handleRepostSlack()}
            isReposting={announceLoading}
            justPosted={justPosted}
            preview={preview ?? null}
            previewLoading={previewLoading}
            previewError={Boolean(previewError)}
            multipleDraftsDetected={multipleDraftsDetected}
            onPublishConfirmed={handlePublishDraft}
            onDiscardConfirmed={handleDiscardDraft}
            onToggleChecklistItem={(section, index, checked, lineContent) =>
              void handleToggleChecklistItem(section, index, checked, lineContent)
            }
          />
        ) : (
          /* Pending release card */
          <section aria-label="Pending release" className="border-border-primary border">
            {!previewCalled ? (
              /*
                Idle CTA — preview generation is deferred until the operator
                explicitly opts in. This is the cold-start state when no draft
                exists and the operator hasn't asked for one yet. Avoids
                burning an LLM cache miss every time someone opens /releases
                to glance at the history table or the open draft.

                The lightweight changelog (commits + base/head SHA + CI
                status) is fetched eagerly via `adminGetReleaseChangeLog`
                and rendered below the button so operators can preview
                pending changes before committing to release-notes
                generation.
              */
              <div className="flex flex-col gap-6 px-6 py-10">
                <div className="flex flex-col items-center gap-4">
                  <div className="flex max-w-md flex-col items-center gap-1 text-center">
                    <h2 className="text-fg-primary text-base font-semibold">No release in progress</h2>
                    <p className="text-fg-secondary text-sm">
                      Start a new draft to generate release notes from commits since the last release.
                    </p>
                  </div>
                  <Button color="primary" size="md" onClick={() => void triggerPreview()}>
                    Start a draft release
                  </Button>
                </div>

                {/*
                  Pending-changes preview. Loading state is intentionally
                  understated — the operator's primary action is the button
                  above; the changelog is reference material.
                */}
                <div className="border-border-primary border-t pt-5">
                  {changeLogLoading && !changeLog ? (
                    <span className="text-fg-tertiary text-xs italic">Loading pending changes…</span>
                  ) : !changeLog ? (
                    <span className="text-fg-tertiary text-xs italic">Pending changes unavailable.</span>
                  ) : changeLog.commits.length === 0 ? (
                    <span className="text-fg-tertiary text-xs italic">
                      No commits since {changeLog.baseTag || 'the last release'} — nothing to release.
                    </span>
                  ) : (
                    <div className="flex flex-col gap-3">
                      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                        <div className="flex flex-col">
                          <span className="text-fg-tertiary text-[10px] uppercase tracking-widest">Next tag</span>
                          <span className="text-fg-primary font-mono text-base font-semibold">
                            {changeLog.computedNextTag}
                          </span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-fg-tertiary text-[10px] uppercase tracking-widest">Reviewed head</span>
                          <span className="text-fg-secondary font-mono text-xs">
                            {changeLog.headSha.slice(0, SHORT_SHA_LENGTH)}
                          </span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-fg-tertiary text-[10px] uppercase tracking-widest">Base</span>
                          <span className="text-fg-secondary font-mono text-xs">
                            {changeLog.baseTag} ({changeLog.baseSha.slice(0, SHORT_SHA_LENGTH)})
                          </span>
                        </div>
                        <div className="self-center">
                          <CiStatusBadge
                            conclusion={changeLog.ciStatus.conclusion}
                            successCount={changeLog.ciStatus.successCount}
                            failureCount={changeLog.ciStatus.failureCount}
                            pendingCount={changeLog.ciStatus.pendingCount}
                            totalCount={changeLog.ciStatus.totalCount}
                          />
                        </div>
                      </div>
                      <CommitsBlock commits={changeLog.commits} baseTag={changeLog.baseTag} defaultExpanded={false} />
                    </div>
                  )}
                </div>
              </div>
            ) : previewLoading ? (
              /*
                Generating state — covers the in-flight spinner from the moment
                the start mutation fires through to the COMPLETED poll. The
                loading branch runs **before** `previewError` so a Retry click
                swaps the scene back to the spinner immediately while the new
                start mutation is in flight.
              */
              renderGeneratingSpinner()
            ) : pollingTimedOut && !preview ? (
              /*
                Polling cap hit (default 90 s) without the worker writing a
                cache row. Either the worker is still in flight against an
                outlier-large diff, or it errored silently. Operator-visible
                recovery: click Retry to re-fire the start mutation
                idempotently — on cache hit (most likely if the worker
                eventually finished) we flip straight to the rendered
                preview; on miss we re-dispatch the worker.
              */
              <div className="flex flex-col items-center gap-4 px-6 py-12">
                <div className="flex max-w-md flex-col items-center gap-1 text-center">
                  <h2 className="text-fg-primary text-base font-semibold">Still generating release notes</h2>
                  <p className="text-fg-secondary text-sm">
                    The worker is taking longer than 90 seconds. Click Retry to check again — if it has finished, the
                    preview will load instantly. If it consistently fails, check Sentry for the underlying error.
                  </p>
                </div>
                <Button color="primary" size="md" onClick={() => void refetchPreview()}>
                  Retry
                </Button>
              </div>
            ) : previewError ? (
              /*
                Error state — covers a failed start mutation (couldn't reach
                the Fargate worker) and any pollError. Without this branch
                the operator would be stuck on the spinner forever. Click
                Retry to re-fire the start mutation.
              */
              <div className="flex flex-col items-center gap-4 px-6 py-12">
                <div className="flex max-w-md flex-col items-center gap-1 text-center">
                  <h2 className="text-fg-error-primary text-base font-semibold">Failed to generate release notes</h2>
                  <p className="text-fg-secondary text-sm">
                    {previewError.message || 'The release-notes pipeline returned an error.'} Retry, or check Sentry for
                    the underlying failure.
                  </p>
                </div>
                <Button color="primary" size="md" onClick={() => void refetchPreview()}>
                  Retry
                </Button>
              </div>
            ) : !preview ? (
              /*
                Post-response interstitial: the response arrived but
                Apollo hasn't written the cache yet, so `data` is still
                undefined. Same spinner as the loading state.
              */
              renderGeneratingSpinner()
            ) : (
              <>
                {/*
                Sticky action header — tag identity, base/head SHAs, CI status,
                and the Announce button. Stays pinned to viewport top while
                scrolling the body so the operator never loses sight of the
                action button.

                Solid `bg-app` matches the body — mops-web's `bg-bg-*` semantic
                tokens use alpha in dark mode (designed to overlay, not be
                opaque), so a translucent sticky would let scrolling content
                bleed through. The shadow + bottom border provide the visual
                "elevated" cue.
              */}
                <div className="bg-app border-border-primary sticky top-0 z-10 border-b px-4 py-3 shadow-lg shadow-black/40">
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                    <div className="flex flex-col">
                      <span className="text-fg-tertiary text-[10px] uppercase tracking-widest">Next tag</span>
                      <span className="text-fg-primary font-mono text-2xl font-semibold">
                        {preview.computedNextTag}
                      </span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-fg-tertiary text-[10px] uppercase tracking-widest">Reviewed head</span>
                      <span className="text-fg-secondary font-mono text-sm">
                        {preview.headSha.slice(0, SHORT_SHA_LENGTH)}
                      </span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-fg-tertiary text-[10px] uppercase tracking-widest">Base</span>
                      <span className="text-fg-secondary font-mono text-sm">
                        {preview.baseTag} ({preview.baseSha.slice(0, SHORT_SHA_LENGTH)})
                      </span>
                    </div>
                    <div className="self-center">
                      <CiStatusBadge
                        conclusion={preview.ciStatus.conclusion}
                        successCount={preview.ciStatus.successCount}
                        failureCount={preview.ciStatus.failureCount}
                        pendingCount={preview.ciStatus.pendingCount}
                        totalCount={preview.ciStatus.totalCount}
                      />
                    </div>
                    <div className="ml-auto flex flex-wrap items-center justify-end gap-3">
                      {justPosted && (
                        <span className="text-fg-secondary text-xs">
                          Posted at {formatPostedAtClockTime(justPosted.postedAt)}
                          {justPosted.slackUrl && (
                            <>
                              {' — '}
                              <a
                                href={justPosted.slackUrl}
                                target="_blank"
                                rel="noreferrer noopener"
                                className="text-fg-brand-primary hover:text-fg-brand-primary_alt underline underline-offset-2"
                              >
                                view in Slack
                              </a>
                            </>
                          )}
                        </span>
                      )}
                      {buttonState && buttonState.mode === 'disabled' ? (
                        <Tooltip title={buttonState.tooltip ?? ''} placement="top">
                          <TooltipTrigger>
                            <span className="inline-block">
                              <Button color="secondary" size="sm" isDisabled>
                                {buttonState.label}
                              </Button>
                            </span>
                          </TooltipTrigger>
                        </Tooltip>
                      ) : (
                        <Button
                          color="primary"
                          size="sm"
                          isLoading={announceLoading || isWaitingForDraftPropagation}
                          isDisabled={!buttonState || draftLoading}
                          onClick={() => void handleAnnounce()}
                        >
                          {isWaitingForDraftPropagation
                            ? 'Waiting for draft to propagate…'
                            : (buttonState?.label ?? `Announce pre-release in ${SLACK_CHANNEL}`)}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/*
                Body. Risks first (banner), then summary (full width), then a
                two-column workflow grid (prereqs | test plan), then commits
                (collapsed reference data).
              */}
                <div className="flex flex-col gap-5 px-4 py-4">
                  <RiskCalloutsBanner riskCallouts={preview.riskCallouts} />

                  <div className="flex flex-col gap-2">
                    <h2 className="text-fg-tertiary text-xs font-medium uppercase tracking-wider">Summary</h2>
                    {preview.summary.length === 0 ? (
                      <p className="text-fg-tertiary text-xs italic">No summary generated.</p>
                    ) : (
                      <ul className="text-fg-primary marker:text-fg-tertiary flex list-disc flex-col gap-1 pl-5 text-sm">
                        {preview.summary.map((line, index) => (
                          <li key={index}>
                            <InlineMarkdown text={line} />
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* MOPS targets a 1280px minimum viewport (see mops-web/AGENTS.md), so the prereqs / test-plan two-column grid is unconditional — no responsive breakpoint. */}
                  <div className="grid grid-cols-2 gap-x-6 gap-y-5">
                    <div className="flex flex-col gap-2">
                      <h2 className="text-fg-tertiary text-xs font-medium uppercase tracking-wider">Prerequisites</h2>
                      {/*
                      Prereqs are extracted by the LLM from squash-merge commit
                      bodies. The model sometimes invents steps or misses one
                      buried in a long body — set that expectation **above**
                      the list so an unfamiliar release manager reads the
                      caveat before scanning the items, not after.
                    */}
                      {preview.prerequisites.length > 0 && (
                        <p className="text-fg-quaternary text-[11px] italic">
                          ⚠ Generated from PR bodies — verify against the source PR before relying on these.
                        </p>
                      )}
                      <PrerequisitesChecklist
                        items={preview.prerequisites}
                        commits={preview.commits}
                        emptyCopy="No prerequisites required for this release."
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <h2 className="text-fg-tertiary text-xs font-medium uppercase tracking-wider">
                        Suggested pre-release test plan
                      </h2>
                      <TestPlanChecklist
                        items={preview.testPlan}
                        emptyCopy="No frontend-verifiable test plan items for this release."
                      />
                    </div>
                  </div>

                  <CommitsBlock commits={preview.commits} baseTag={preview.baseTag} />
                </div>
              </>
            )}
          </section>
        )}

        {/* Release history */}
        <section aria-label="Release history" className="flex flex-col gap-2">
          <h2 className="text-fg-tertiary text-xs font-medium uppercase tracking-wider">Release history</h2>
          {historyLoading && history.length === 0 ? (
            <span className="text-fg-secondary text-xs">Loading…</span>
          ) : history.length === 0 ? (
            <span className="text-fg-tertiary text-xs italic">No releases yet.</span>
          ) : (
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Tag</Table.HeaderCell>
                  <Table.HeaderCell>Date</Table.HeaderCell>
                  <Table.HeaderCell>Drafted by</Table.HeaderCell>
                  <Table.HeaderCell>Summary</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {history.map((release) => (
                  <HistoryRow key={release.tag} release={release} />
                ))}
              </Table.Body>
            </Table>
          )}
        </section>
      </main>
    </div>
  );
};

export default ReleasesScene;
